import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faEnvelope, faEye, faPrint, faXmark } from "@fortawesome/free-solid-svg-icons";
import { RecibeApi, RecibeDataType } from "@/api/RecibeApi";
import { formatMoneyValue } from "@utils/Currency";
import { getPaymentMethodLabel } from "@utils/Utils";
import SendDocumentToEmailOffCanvas from "@components/layout/Offcanvas/SendDocumentToEmailOffCanvas";
import { OpenSendDocumentPubSub } from "@utils/pubsubs/PubSub";
import { useAppSelector } from "@/store/hooks";
import { checkRecibeTicketPrint } from "@utils/ticketPrintUtils";
import { TICKET_PRINT_FORMANT_ID } from "@utils/Constants";
import moment from "moment";
import LoadingScreen from "@components/content/LoadingScreen";
import { SettlementApi } from "@/api/SettlementApi";
import { InvoiceApi } from "@/api/InvoiceApi";
import { InvoicePaymentApi } from "@/api/InvoicePaymentApi";
import { SettlementPaymentApi } from "@/api/SettlementPaymentApi";
import { InterestEntryApi } from "@/api/InterestEntryApi";
import { showAlert } from "@utils/Alert";
import { RecibeFormDataKeys } from "@/generated/RecibeFormDataKeys";
import { RecibeRelatedAttributes } from "@/formTypes/RecibeDataType";

const RecibeIndex = () => {
  const navigate = useNavigate();
  const modelRef = useRef<ModelIndexHandle>(null);
  const suscriptionData = useAppSelector(state => state.suscription.suscription)
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const suscriptionLoaded = suscriptionData?.id && suscriptionData?.order_recibe_by_created;
  const sortSetup = suscriptionLoaded ? ["createdAt:desc", "numero:desc"] : ["created:desc", "numero:desc"]

  useEffect(() => {
  }, []);

  const triggerEmailSend = async (item: RecibeDataType) => {
    const selectionSet = [
      ...RecibeFormDataKeys.filter(k => !RecibeRelatedAttributes.includes(k)),
      "currency.*",
      "client.*",
    ]
    const recibeData = await RecibeApi.get(`${item.id}`, { selectionSet })
    OpenSendDocumentPubSub.publish({
      openOffCanvas: true,
      emails: recibeData.client?.email || "",
      sourceURL: `/user/recibes/print/${item.id}/0/1`,
      emailProps: {
        emailBody: suscriptionData?.recibe_email || "",
        emailFilename: `Recibo #${item.numero}`,
        emailSubject: `Recibo #${item.numero}`,
        Suscription: {
          email: suscriptionData?.email || ""
        }
      }
    })
  }

  const triggerPrintRecibe = (item: RecibeDataType) => {
    if (suscriptionData?.id && item?.id) {
      if (suscriptionData.formato_impresion == TICKET_PRINT_FORMANT_ID) {
        checkRecibeTicketPrint(suscriptionData, item)
      } else {
        window.open(`/user/recibes/print/${item.id}`)
      }
    }
  }

  const cancelRecibe = async (cancelRecibe: RecibeDataType) => {
    try {
      setIsLoading(true)
      const updatePromises = []
      const recibeId = `${cancelRecibe.id}`;
      const { items: settlements } = await SettlementApi.fetchAllByRecibe(recibeId)
      const { items: invoices } = await InvoiceApi.fetchAllByRecibe(recibeId)
      const { items: invoicePayments } = await InvoicePaymentApi.fetchAllByRecibe(recibeId)
      const { items: settlementsPayments } = await SettlementPaymentApi.fetchAllByRecibe(recibeId)
      const { items: interests } = await InterestEntryApi.fetchAllByRecibe(recibeId)

      updatePromises.push(RecibeApi.update(recibeId, { anulado: true }))

      const settlementPromises: any[] = settlements.map(s => SettlementApi.update(`${s.id}`, {
        pagada: false,
        status: "pendiente",
        clientId: `${s.clientId}`,
        fecha: `${s.fecha}`,
        user: s.user,
        recibeId: null
      }), true)
      updatePromises.push(...settlementPromises)

      const invoicesPromises: any[] = invoices.map(s => InvoiceApi.update(`${s.id}`,
        {
          pagada: false,
          status: "pendiente",
          clientId: `${s.clientId}`,
          recibeId: null,
          fecha: s.fecha,
          user: s.user
        },
        true))
      updatePromises.push(...invoicesPromises)

      const invoicePaymentsPromises: any[] = invoicePayments.map(s => InvoicePaymentApi.update(`${s.id}`, { estado: "cancelled" }))
      updatePromises.push(...invoicePaymentsPromises)

      const settlementPaymentsPromises: any[] = settlementsPayments.map(s => SettlementPaymentApi.update(`${s.id}`, { estado: "cancelled" }))
      updatePromises.push(...settlementPaymentsPromises)

      const interestsPromises: any[] = interests.flatMap(s => {
        const interestPromises = []
        interestPromises.push(InterestEntryApi.update(`${s.id}`, { anulado: true }))
        if (s.settlementInterestId) {
          interestPromises.push(SettlementApi.update(`${s.settlementInterestId}`, { anulada: true, status: "anulada", fecha: s.created }))
        }
        return interestPromises
      })
      updatePromises.push(...interestsPromises)

      const promisesResult = await Promise.all(updatePromises);

      showAlert("success", "El recibo ha sido cancelado correctamente")
      modelRef.current?.refetch()

    } catch (err) {
      if (err instanceof Error) {
        showAlert("danger", "No fue posible anular recibo")
      }
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <PageContainer
      title="Lista de Recibos"
      backUrl="/user/dashboard-accounting"
      primaryActions={[
        {
          onClick: () => {
            navigate('/user/recibes/add')
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ]}
    >
      {!!suscriptionData?.id && (
        <ModelIndex<RecibeDataType>
          ref={modelRef}
          meiliConfig={{
            index: "recibes",
            sort: sortSetup
          }}
          fetchData={(...args) => RecibeApi.fetchAll(...args)}
          filterProps={[
            {
              key: "numero",
              filterType: "eq"
            }
          ]}
          primaryProperties={[
            {
              key: "numero",
              highlighted: true,
              optionalHighlighted: (val: any, data: RecibeDataType) => { return data.anulado || false },
              highlightedColors: {
                "true": {
                  "bg": "#dc3545",
                  "text": "#fff"
                },
                "false": {
                  "bg": "#337ab7",
                  "text": "#fff"
                }
              },
            },
            {
              key: (data) => {
                if ("clientNombre" in data) {
                  return (data as any).clientNombre
                }

                return data.nombre || data.client?.nombre || ""
              },
              alignment: "Start"
            },
          ]}
          secondaryProperties={[
            {
              key: (data) => {
                const date = moment(data.created, "YYYY-MM-DD")
                return date.format("DD-MM-YYYY")
              },
              title: "Fecha",
            },
            {
              key: (data) => getPaymentMethodLabel(data.forma_pago),
              title: "Forma Pago",
            },
            {
              key: (data) => {
                if ("currency_id" in data) {
                  return formatMoneyValue(data.abono, (data as any).currency_id)
                }
                return formatMoneyValue(data.abono, data.currency.currency_id)
              },
              title: "Total",
              highlighted: true
            }
          ]}
          listActions={[
            {
              isVisible: (data) => !data.anulado,
              onClick: (id, item) => cancelRecibe(item),
              actionTitle: "Anular Recibo",
              iconProps: { icon: faXmark },
              needConfirm: true,
              confirmProps: {
                confirmMessage: "Esta seguro que desea anular este Recibo? ",
              }
            },
            {
              onClick: (id, item) => triggerEmailSend(item),
              actionTitle: "Enviar Por Correo ",
              iconProps: { icon: faEnvelope }
            },
            {
              href: "/user/recibes/view/{:id}",
              actionTitle: "Ver Recibo",
              target: "_blank",
              iconProps: { icon: faEye }
            },
            {
              onClick: (id, item) => triggerPrintRecibe(item),
              actionTitle: "Imprimir Recibo",
              target: "_blank",
              iconProps: { icon: faPrint }
            }
          ]}
        >
        </ModelIndex>
      )}

      <SendDocumentToEmailOffCanvas
        title="Envio de Recibo a Email" />
      <LoadingScreen isLoading={isLoading} />
    </PageContainer >
  );
}

export default RecibeIndex;
